import { useEffect, useState, type KeyboardEvent } from 'react'
import { toast } from 'sonner'
import { StickyNote } from 'lucide-react'
import { useCreateNote } from '@/hooks/use-notes'
import type { PageEntity } from '@/contexts/PageContext'
import type { CreateNoteInput } from '@/types/api'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'

type NoteTarget = { entity_type: CreateNoteInput['entity_type']; entity_id: string }

// The /notes router only links to party / engagement / pursuit records. Map the
// on-screen page entity to the note target it belongs on; return null when the
// current screen has nothing a note can attach to (a list view, or a record type
// notes does not cover yet).
export function noteEntityFor(entity: PageEntity): NoteTarget | null {
  if (!entity) return null
  switch (entity.type) {
    case 'contact':
    case 'party':
      return { entity_type: 'party', entity_id: entity.id }
    case 'opportunity':
    case 'engagement':
      return { entity_type: 'opportunity', entity_id: entity.id }
    case 'pursuit':
      return { entity_type: 'opportunity', entity_id: entity.id }
    default:
      return null
  }
}

export function QuickCaptureDialog({
  open, entity, onOpenChange,
}: {
  open: boolean
  entity: PageEntity
  onOpenChange: (open: boolean) => void
}) {
  const target = noteEntityFor(entity)
  const [content, setContent] = useState('')
  const createNote = useCreateNote()

  // Reset the draft each time the dialog opens so a fresh capture starts clean.
  useEffect(() => {
    if (open) setContent('')
  }, [open])

  const submit = () => {
    const text = content.trim()
    if (!text || !target || createNote.isPending) return
    createNote.mutate(
      { ...target, content: text },
      {
        onSuccess: () => {
          toast.success(entity ? `Note saved to ${entity.name}` : 'Note saved')
          onOpenChange(false)
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Could not save that note, give it another go')
        },
      },
    )
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      submit()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StickyNote className="h-4 w-4 text-muted-foreground" />
            Quick note
          </DialogTitle>
          {target && entity ? (
            <DialogDescription>
              Attaching to <span className="font-medium text-foreground">{entity.name}</span>
            </DialogDescription>
          ) : (
            <DialogDescription>
              Open a contact or opportunity to pin a note to it, or ask Friday to capture it for you.
            </DialogDescription>
          )}
        </DialogHeader>

        {target ? (
          <>
            <Textarea
              autoFocus
              rows={4}
              aria-label="Note"
              placeholder="What do you want to remember?"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <DialogFooter className="items-center gap-2 sm:justify-between">
              <span className="text-xs text-muted-foreground">⌘ Enter to save</span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={createNote.isPending}>
                  Cancel
                </Button>
                <Button onClick={submit} disabled={!content.trim() || createNote.isPending}>
                  {createNote.isPending ? 'Saving…' : 'Save note'}
                </Button>
              </div>
            </DialogFooter>
          </>
        ) : (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
